import {getTestProps} from '@benchling/js-test-utils';
import {
  Alert,
  Box,
  Button,
  createStyles,
  Group,
  PasswordInput,
  Select,
  Stack,
  TextInput,
  Tooltip,
} from '@mantine/core';
import React from 'react';

import {
  DEPARTMENT_OPTIONS,
  PASSWORD_HINT,
  PASSWORD_MIN_LENGTH,
  type AuthMethod,
  type EmailType,
} from '../constants';
import CreditsBanner from '../CreditsBanner';
import SignupStepHeader from '../SignupStepHeader';
import {
  SIGNUP_BUTTON_SIZE,
  SIGNUP_COMPACT_STACK_SPACING,
  SIGNUP_CONTROL_SIZE,
  SIGNUP_FORM_STACK_SPACING,
  SIGNUP_STEP_STACK_SPACING,
  signupControlClassNames,
  useSignupStepStyles,
} from '../signupUi';
import UpsellCallout from '../UpsellCallout';

const useStyles = createStyles((theme) => ({
  // The Nucleus PasswordInput theme strips the outer border (expecting a global monolith LESS rule
  // to border the inner input) — that rule isn't loaded in the standalone Model Hub bundle, so the
  // field renders with no visible box. Re-apply the border here, mirroring
  // ui-library/inputs/SecureTextInput.
  input: {
    '&&': {
      border: `1px solid ${theme.colors.gray[4]} !important`,
      borderRadius: `${theme.radius.sm} !important`,
    },
  },
  // Visually-hidden username field: the email was collected on a prior step, so password managers
  // otherwise bind credentials to the wrong field (e.g. Last name). A rendered (not display:none)
  // input carrying the email with autoComplete="username" steers them to the right identity.
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
}));

interface ProfileStepProps {
  authMethod: AuthMethod;
  department: string | null;
  email: string;
  emailType: EmailType;
  error: string | null;
  firstName: string;
  lastName: string;
  password: string;
  onBack: () => void;
  onDepartmentChange: (value: string | null) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  onUseWorkEmail: () => void;
}

export default function ProfileStep({
  authMethod,
  department,
  email,
  emailType,
  error,
  firstName,
  lastName,
  password,
  onBack,
  onDepartmentChange,
  onFirstNameChange,
  onLastNameChange,
  onPasswordChange,
  onSubmit,
  onUseWorkEmail,
}: ProfileStepProps): JSX.Element {
  const {classes: profileClasses} = useStyles();
  const {classes: stepClasses, cx} = useSignupStepStyles();
  const controlClassNames = signupControlClassNames(stepClasses);
  const isGoogleSignup = authMethod === 'google';
  const hasValidPassword = isGoogleSignup || password.length >= PASSWORD_MIN_LENGTH;
  const canSubmit = firstName.trim() !== '' && lastName.trim() !== '' && hasValidPassword;

  return (
    <Stack
      id="model-hub-signup-profile-form"
      spacing={SIGNUP_STEP_STACK_SPACING}
      {...getTestProps({component: 'ModelHubSignup', element: 'profileForm'})}
    >
      <SignupStepHeader title="Set up your profile" onBack={onBack} />
      {error != null && (
        <Alert color="red" role="alert" title="Unable to continue">
          {error}
        </Alert>
      )}
      <Stack spacing={SIGNUP_COMPACT_STACK_SPACING}>
        <CreditsBanner emailType={emailType} />
        {emailType !== 'work' && <UpsellCallout emailType={emailType} onUseWorkEmail={onUseWorkEmail} />}
      </Stack>
      <Stack spacing={SIGNUP_FORM_STACK_SPACING}>
        {/* Hidden username hint for password managers — the email lives on a prior step. */}
        <input
          aria-hidden={true}
          autoComplete="username"
          className={profileClasses.visuallyHidden}
          id="model-hub-signup-profile-email"
          readOnly={true}
          tabIndex={-1}
          type="email"
          value={email}
          {...getTestProps({component: 'ModelHubSignup', element: 'usernameHint'})}
        />
        <Group grow={true}>
          <TextInput
            autoComplete="given-name"
            autoFocus={true}
            classNames={controlClassNames}
            label="First name"
            placeholder="Ada"
            size={SIGNUP_CONTROL_SIZE}
            value={firstName}
            onChange={(event) => {
              onFirstNameChange(event.currentTarget.value);
            }}
          />
          <TextInput
            autoComplete="family-name"
            classNames={controlClassNames}
            label="Last name"
            placeholder="Lovelace"
            size={SIGNUP_CONTROL_SIZE}
            value={lastName}
            onChange={(event) => {
              onLastNameChange(event.currentTarget.value);
            }}
          />
        </Group>
        {!isGoogleSignup && (
          <PasswordInput
            autoComplete="new-password"
            classNames={{
              ...controlClassNames,
              innerInput: stepClasses.controlInput,
              input: cx(stepClasses.controlInput, profileClasses.input),
            }}
            description={PASSWORD_HINT}
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            label="Password"
            placeholder={'•'.repeat(PASSWORD_MIN_LENGTH)}
            size={SIGNUP_CONTROL_SIZE}
            value={password}
            onChange={(event) => {
              onPasswordChange(event.currentTarget.value);
            }}
          />
        )}
        <Select
          classNames={controlClassNames}
          clearable={true}
          data={DEPARTMENT_OPTIONS}
          label="Department"
          placeholder="Select your team…"
          size={SIGNUP_CONTROL_SIZE}
          value={department}
          onChange={onDepartmentChange}
        />
      </Stack>
      <Tooltip
        disabled={hasValidPassword}
        label={`Use at least ${PASSWORD_MIN_LENGTH} characters for your password.`}
        withArrow={true}
      >
        <Box
          mt={SIGNUP_STEP_STACK_SPACING}
          {...getTestProps({component: 'ProfileStep', element: 'passwordTooltipTarget'})}
        >
          <Button disabled={!canSubmit} fullWidth={true} size={SIGNUP_BUTTON_SIZE} onClick={onSubmit}>
            Continue
          </Button>
        </Box>
      </Tooltip>
    </Stack>
  );
}
